import { get, isEqual, set } from 'lodash';
import { ACCOUNT_ID, DIMENSION } from 'hooks';
import { campaignNameValidations, ENTITY } from 'modules/campaigns/config';
import { CAMPAIGNS_ROUTE } from 'modules/campaigns/config/routes';
import { REPORT_ENTITY_IDS } from 'modules/campaigns/hooks/useReportEntityIds';
import { REPORT_ENTITY_TYPE } from 'modules/campaigns/hooks/useReportEntityType';
import { setSelectedRows } from 'modules/campaigns/modules/campaigns-reports/actions';
import { duplicateCampaign, saveCampaign } from 'modules/campaigns/modules/campaigns-reports/flows';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { SPENDING_LIMITS } from 'modules/campaigns/modules/common-campaign-form/config/spendingLimits';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { isProgrammaticAccount } from '../../../../../../account-management/accountType';
import { PERMISSIONS_OPERATORS } from '../../../../../../taboola-common-frontend-modules/authentication';
import {
    ActionableTextWithInternalLinkCellRenderer,
    CampaignActionsCellRenderer,
    CampaignStatusRenderer,
    CheckboxCellRenderer,
    CheckboxHeaderRenderer,
    FormattedMessageHeaderRenderer,
    FormattedMessageWithTooltipHeaderRenderer,
    formattedNumberValueGetter,
    gridNumberFormatter,
    StateCellRenderer,
    withCampaignEditIndication,
} from '../../../components';
import { DATE_PRESET, END_DATE, REPORT_ID, START_DATE } from '../../../hooks';
import {
    actionsColumnType,
    checkBoxSelectionColumn,
    editableColumn,
    keyColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from '../../columns';
import * as FIELDS from '../../fields';
import { nonDefaultHeaderComponentParams } from '../../fields';
import {
    isBidEditable,
    isBudgetEditable,
    isCampaignEditable,
    isSpendingLimitEditable,
    onlyNetworkColumn,
} from '../../predicates';
import { CREATIVE_REPORT_ID } from '../creative/creativeReportId';
import { CAMPAIGN_REALTIME_REPORT_ID } from './campaignRealtimeReportId';

const creativeReportId = CREATIVE_REPORT_ID;
const isDisabled = data => !isCampaignEditable({ data });

const campaignReportColumnDefinition = [
    ...[
        {
            headerComponent: CheckboxHeaderRenderer,
            headerName: FIELDS.IS_SELECTED.label,
            field: FIELDS.IS_SELECTED.field,
            type: [leftPinnedColumn, checkBoxSelectionColumn, staticColumn],
            cellRendererParams: {
                onClick: setSelectedRows,
                isDisabled,
            },
            headerComponentParams: {
                onClick: setSelectedRows,
                isDisabled,
            },
            cellRenderer: CheckboxCellRenderer,
            csv: false,
            permissions: {
                permissions: ['CAMPAIGNS_BULK_UPDATE', 'CAMPAIGNS_BULK_UPDATE_NON_ATOMIC'],
                operator: PERMISSIONS_OPERATORS.AND,
            },
            accountConfigurations: {
                [FEATURE_FLAGS.CAMPAIGNS_BULK_UPDATE_API_ENABLED_KEY]: 'true',
            },
        },
        {
            headerName: FIELDS.IS_ACTIVE.label,
            field: FIELDS.IS_ACTIVE.field,
            type: [toggleColumn, leftPinnedColumn, staticColumn, lockedColumn],
            cellRendererParams: {
                ...FIELDS.IS_ACTIVE.renderParams,
                isDisabled,
                reportId: CAMPAIGN_REALTIME_REPORT_ID,
            },
            cellRenderer: StateCellRenderer,
            csv: false,
        },
        {
            headerName: FIELDS.ID.label,
            field: FIELDS.ID.field,
            type: [numericColumn],
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: nonDefaultHeaderComponentParams(CAMPAIGN_REALTIME_REPORT_ID),
            sortable: false,
        },
        {
            headerName: FIELDS.DEMAND_TYPE.label,
            field: FIELDS.DEMAND_TYPE.field,
            cellRendererParams: {
                msgIdPrefix: 'app.campaigns.campaign.demand.type',
            },
            valueFormatter: FIELDS.DEMAND_TYPE.valueFormatter,
            csvValueGetter: FIELDS.DEMAND_TYPE.csvValueGetter,
            visibilityCondition: ({ selectedAccount }) => isProgrammaticAccount(selectedAccount),
            type: [leftPinnedColumn],
        },
        {
            headerName: FIELDS.NAME.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.NAME.headerComponentParams,
            field: FIELDS.NAME.field,
            type: [leftPinnedColumn, keyColumn, editableColumn],
            editable: isCampaignEditable,
            cellRenderer: ActionableTextWithInternalLinkCellRenderer,
            cellRendererParams: {
                lines: 2,
                to: CAMPAIGNS_ROUTE,
                paramsToKeep: [ACCOUNT_ID, DIMENSION, START_DATE, END_DATE, DATE_PRESET],
                paramsFactory: ({ id }) => ({
                    [REPORT_ID]: creativeReportId,
                    [REPORT_ENTITY_IDS]: id,
                    [REPORT_ENTITY_TYPE]: ENTITY.CAMPAIGN,
                }),
                isSearchable: true,
            },
            cellEditor: FIELDS.NAME.cellEditor,
            cellEditorParams: {
                validations: campaignNameValidations,
                editableTextClassName: 'with-formatted-spaces',
            },
        },
        {
            headerName: FIELDS.STATUS.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.STATUS.headerComponentParams,
            field: FIELDS.STATUS.field,
            type: [leftPinnedColumn],
            cellRenderer: CampaignStatusRenderer,
            width: 180,
            minWidth: 56,
            cellRendererParams: FIELDS.STATUS.renderParams,
            csvValueGetter: FIELDS.STATUS.csvValueGetter,
            // TODO: DEV-71981 change valueGetter/equals once a generic solution is implemented
            valueGetter: params => ({
                status: get(params, 'data.status'),
                isActive: get(params, 'data.isActive'),
                creativeTrafficAllocationMethod: get(params, 'data.creativeTrafficAllocationMethod'),
                creativeTrafficAllocationABTestEndDate: get(params, 'data.creativeTrafficAllocationABTestEndDate'),
            }),
            equals: isEqual,
        },
        {
            headerName: FIELDS.ACCOUNT_ID.label,
            field: FIELDS.ACCOUNT_ID.field,
            visibilityCondition: onlyNetworkColumn,
        },
        {
            headerName: FIELDS.ACCOUNT_DESCRIPTION.label,
            field: FIELDS.ACCOUNT_DESCRIPTION.field,
            cellRenderer: FIELDS.ACCOUNT_DESCRIPTION.cellRenderer,
            cellRendererParams: FIELDS.ACCOUNT_DESCRIPTION.cellRendererParams,
            visibilityCondition: onlyNetworkColumn,
        },
        {
            headerName: FIELDS.CAMPAIGNS_GROUP_NAME.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.CAMPAIGNS_GROUP_NAME.headerComponentParams,
            field: FIELDS.CAMPAIGNS_GROUP_NAME.field,
            cellRenderer: FIELDS.CAMPAIGNS_GROUP_NAME.cellRenderer,
            cellRendererParams: FIELDS.CAMPAIGNS_GROUP_NAME.cellRendererParams,
        },
        {
            headerName: FIELDS.CAMPAIGN_CONVERSION_RULES.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.CAMPAIGN_CONVERSION_RULES.headerComponentParams,
            field: FIELDS.CAMPAIGN_CONVERSION_RULES.field,
            cellRenderer: FIELDS.CAMPAIGN_CONVERSION_RULES.cellRenderer,
            valueFormatter: FIELDS.CAMPAIGN_CONVERSION_RULES.valueFormatter,
            accountConfigurations: FIELDS.CAMPAIGN_CONVERSION_RULES.accountConfigurations,
        },
        {
            headerName: FIELDS.BUDGET.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.BUDGET.headerComponentParams,
            field: FIELDS.BUDGET.field,
            type: [numericColumn, editableColumn],
            editable: isBudgetEditable,
            cellRenderer: FIELDS.BUDGET.cellRenderer,
            cellRendererParams: FIELDS.BUDGET.renderParams,
            csv: FIELDS.BUDGET.csv,
            cellEditor: FIELDS.BUDGET.cellEditor,
            cellEditorParams: {
                fieldPath: 'budget.value',
            },
            valueGetter: 'data.budget.value',
            valueSetter: params => set(params, 'data.budget.value', +params.newValue),
        },
        {
            headerName: FIELDS.SPENDING_LIMIT.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.SPENDING_LIMIT.headerComponentParams,
            field: FIELDS.SPENDING_LIMIT.field,
            dataField: FIELDS.BUDGET.field,
            type: [editableColumn, numericColumn],
            editable: isSpendingLimitEditable,
            cellRenderer: FIELDS.SPENDING_LIMIT.cellRenderer,
            cellRendererParams: FIELDS.SPENDING_LIMIT.renderParams,
            csv: FIELDS.SPENDING_LIMIT.csv,
            cellEditor: FIELDS.SPENDING_LIMIT.cellEditor,
            valueGetter: 'data.budget.spendingLimit.value',
            cellEditorParams: {
                fieldPath: 'budget.spendingLimit.value',
            },
            valueSetter: params => set(params, 'data.budget.spendingLimit.value', +params.newValue),
        },
        {
            headerName: FIELDS.BID.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.BID.headerComponentParams,
            field: FIELDS.BID.field,
            type: [editableColumn, numericColumn],
            editable: isBidEditable,
            cellRenderer: FIELDS.BID.cellRenderer,
            cellRendererParams: FIELDS.BID.renderParams,
            cellEditor: FIELDS.BID.cellEditor,
            valueParser: ({ newValue }) => Number(newValue),
        },
        {
            headerName: FIELDS.START_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.START_DATE.headerComponentParams,
            field: FIELDS.START_DATE.field,
            valueFormatter: FIELDS.START_DATE.valueFormatter,
            csvValueGetter: FIELDS.START_DATE.csvValueGetter,
        },
        {
            headerName: FIELDS.END_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.END_DATE.headerComponentParams,
            field: FIELDS.END_DATE.field,
            type: [editableColumn],
            editable: isCampaignEditable,
            valueFormatter: FIELDS.END_DATE.valueFormatter,
            csvValueGetter: FIELDS.END_DATE.csvValueGetter,
            cellEditor: FIELDS.END_DATE.cellEditor,
            cellEditorParams: {
                placeholderMsgId: 'campaign.time.frame.no.end.date',
                defaultPlaceholderMsg: 'No End Date',
                inputIconMsgId: 'campaign.time.frame.end',
                defaultInputIconMsg: 'End',
            },
        },
        {
            headerName: FIELDS.BID_STRATEGY.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.BID_STRATEGY.headerComponentParams,
            field: FIELDS.BID_STRATEGY.field,
            cellRenderer: FIELDS.BID_STRATEGY.cellRenderer,
            csvValueGetter: FIELDS.BID_STRATEGY.csvValueGetter,
        },
        {
            headerName: FIELDS.SAFETY_LEVEL.label,
            headerValueGetter: FIELDS.SAFETY_LEVEL.headerValueGetter,
            headerComponentParams: FIELDS.SAFETY_LEVEL.headerComponentParams,
            field: FIELDS.SAFETY_LEVEL.field,
            cellRenderer: FIELDS.SAFETY_LEVEL.cellRenderer,
            cellRendererParams: FIELDS.SAFETY_LEVEL.renderParams,
            csvValueGetter: FIELDS.SAFETY_LEVEL.csvValueGetter,
        },
    ].map(colDef => ({ valueChangeHandler: saveCampaign, ...colDef })),
    ...[
        FIELDS.VISIBLE_IMPRESSIONS,
        FIELDS.CLICKS,
        FIELDS.CONVERSIONS,
        FIELDS.CONVERSIONS_CLICKS,
        FIELDS.CONVERSIONS_VIEWS,
        FIELDS.CPA,
        FIELDS.CPA_GOAL,
        FIELDS.CPA_CLICKS,
        FIELDS.CPA_VIEWS,
        FIELDS.CVR,
        FIELDS.CVR_CLICKS,
        FIELDS.CVR_VIEWS,
        FIELDS.AVERAGE_CPC,
        FIELDS.VCTR,
        FIELDS.VCPM,
        FIELDS.IMPRESSIONS,
        FIELDS.UNIQUE_IMPRESSIONS,
        FIELDS.UNIQUE_VIEWABLE_IMPRESSIONS,
        FIELDS.UNIQUE_CLICKS,
        {
            ...FIELDS.SPENT,
            renderParams: {
                ...FIELDS.SPENT.renderParams,
                barVisibilityCondition: data => get(data, 'budget.type') === SPENDING_LIMITS.LIFETIME,
            },
        },
        FIELDS.CONVERSIONS_VALUE,
        FIELDS.ROAS,
    ].map(fieldConfig => ({
        headerName: fieldConfig.label,
        headerValueGetter: fieldConfig.headerValueGetter || FormattedMessageWithTooltipHeaderRenderer,
        headerComponentParams: fieldConfig.headerComponentParams,
        field: fieldConfig.field,
        type: [numericColumn],
        cellRenderer: fieldConfig.cellRenderer,
        valueFormatter: !fieldConfig.cellRenderer && gridNumberFormatter,
        cellRendererParams: fieldConfig.renderParams,
        csvValueGetter: fieldConfig.csvValueGetter || formattedNumberValueGetter,
        referenceColId: fieldConfig.referenceColId,
        accountConfigurations: fieldConfig.accountConfigurations,
        permissions: fieldConfig.permissions,
        visibilityCondition: fieldConfig.visibilityCondition,
    })),
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        valueGetter: params => params.data,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        cellRendererParams: {
            onDuplicate: duplicateCampaign,
            onTogglePolicyState: saveCampaign,
        },
        cellRenderer: CampaignActionsCellRenderer,
        csv: false,
        minWidth: 100,
        maxWidth: 150,
        width: 150,
    },
];

export default normalizeColumnDefs(
    campaignReportColumnDefinition,
    CAMPAIGN_REALTIME_REPORT_ID,
    withCampaignEditIndication
);
